import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Paper, Box, TextField, Button, FormControl, InputLabel,
    Select, MenuItem, RadioGroup, FormControlLabel, Radio, Grid, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
    Snackbar, Alert, CircularProgress, IconButton, Collapse, Dialog, DialogTitle,
    DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
    Assessment as AssessmentIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    SwapHoriz as SwapHorizIcon
} from '@mui/icons-material';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';

const StockMovementReport = () => {
    const [filters, setFilters] = useState({
        location: '',
        timeFilter: 'today',
        startDate: '',
        endDate: ''
    });
    const [reportData, setReportData] = useState([]);
    const [expandedRow, setExpandedRow] = useState(null);
    const [summary, setSummary] = useState({
        totalPurchased: 0,
        totalSold: 0,
        totalTransferIn: 0,
        totalTransferOut: 0,
        netMovement: 0
    });
    const [availableLocations, setAvailableLocations] = useState([]);
    const [userLocation, setUserLocation] = useState('');
    const [userRole, setUserRole] = useState('');
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, onConfirm: null });
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        const location = localStorage.getItem('userLocation');
        const role = localStorage.getItem('userRole');
        setUserLocation(location);
        setUserRole(role);

        // Set default location for non-Universal employees
        if (location && location !== 'Universal') {
            setFilters(prev => ({ ...prev, location }));
        }

        fetchLocations();
    }, [navigate]);

    const fetchLocations = async () => {
        try {
            const response = await api.get('/config/locations');
            setAvailableLocations(response.data.storageLocations);
        } catch (error) {
            console.error('Error fetching locations:', error);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const validateFilters = (skipWarning = false) => {
        if (userLocation !== 'Universal' && !filters.location) {
            setSnackbar({ open: true, message: 'Location is required', severity: 'error' });
            return { valid: false, needsWarning: false };
        }

        if (filters.timeFilter === 'custom') {
            if (!filters.startDate && !filters.endDate) {
                setSnackbar({ open: true, message: 'Please select at least one date for custom range', severity: 'error' });
                return { valid: false, needsWarning: false };
            }
            if (filters.startDate && filters.endDate && new Date(filters.startDate) > new Date(filters.endDate)) {
                setSnackbar({ open: true, message: 'Start date must be before end date', severity: 'error' });
                return { valid: false, needsWarning: false };
            }

            // Check for 6-month limit
            const sixMonthsAgo = new Date();
            sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
            sixMonthsAgo.setHours(0, 0, 0, 0);

            const startDate = filters.startDate ? new Date(filters.startDate) : new Date();
            if (startDate < sixMonthsAgo) {
                setSnackbar({
                    open: true,
                    message: 'Cannot generate report beyond 6 months. Please select a date within the last 6 months.',
                    severity: 'error'
                });
                return { valid: false, needsWarning: false };
            }

            // Check for 1-month warning (if range is more than 1 month)
            if (!skipWarning && filters.startDate && filters.endDate) {
                const start = new Date(filters.startDate);
                const end = new Date(filters.endDate);
                const diffTime = Math.abs(end - start);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (diffDays > 31) {
                    return { valid: true, needsWarning: true };
                }
            }
        }

        return { valid: true, needsWarning: false };
    };

    const fetchReport = async (skipWarning = false) => {
        const validation = validateFilters(skipWarning);
        if (!validation.valid) return;

        // Show warning confirmation for reports spanning more than 1 month
        if (validation.needsWarning && !skipWarning) {
            setConfirmDialog({
                open: true,
                onConfirm: () => {
                    setConfirmDialog({ open: false, onConfirm: null });
                    fetchReport(true); // Retry with skipWarning=true
                }
            });
            return;
        }

        setLoading(true);
        try {
            const params = {
                timeFilter: filters.timeFilter
            };

            // Add location if selected
            if (filters.location) {
                params.location = filters.location;
            }

            // Add date range for custom filter
            if (filters.timeFilter === 'custom') {
                if (filters.startDate) params.startDate = filters.startDate;
                if (filters.endDate) params.endDate = filters.endDate;
            }

            const response = await api.get('/stock-movement', { params });
            setReportData(response.data);

            // Calculate summary
            const totalPurchased = response.data
                .filter(item => item.type === 'purchase')
                .reduce((sum, item) => sum + item.quantity, 0);

            const totalSold = response.data
                .filter(item => item.type === 'sale')
                .reduce((sum, item) => sum + Math.abs(item.quantity), 0);

            const totalTransferIn = response.data
                .filter(item => item.type === 'transfer_in')
                .reduce((sum, item) => sum + item.quantity, 0);

            const totalTransferOut = response.data
                .filter(item => item.type === 'transfer_out')
                .reduce((sum, item) => sum + Math.abs(item.quantity), 0);

            setSummary({
                totalPurchased: totalPurchased.toFixed(2),
                totalSold: totalSold.toFixed(2),
                totalTransferIn: totalTransferIn.toFixed(2),
                totalTransferOut: totalTransferOut.toFixed(2),
                netMovement: (totalPurchased - totalSold + totalTransferIn - totalTransferOut).toFixed(2)
            });

            if (response.data.length === 0) {
                setSnackbar({ open: true, message: 'No stock movement data found for the selected filters', severity: 'info' });
            }

        } catch (error) {
            console.error('Error fetching stock movement report:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to fetch stock movement report',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleRowExpand = (index) => {
        setExpandedRow(expandedRow === index ? null : index);
    };

    const getStockColor = (stock) => {
        if (stock < 10) return 'error';
        if (stock < 50) return 'warning';
        return 'success';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatPeriod = () => {
        if (filters.timeFilter === 'today') {
            return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } else if (filters.timeFilter === 'custom') {
            const start = filters.startDate ? new Date(filters.startDate).toLocaleDateString('en-GB') : 'Start';
            const end = filters.endDate ? new Date(filters.endDate).toLocaleDateString('en-GB') : 'End';
            return `${start} to ${end}`;
        }
        return '';
    };


    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <AssessmentIcon fontSize="large" />
                    Stock Movement Report
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Track purchases, sales, and transfers for complete inventory management
                </Typography>
            </Box>

            {/* Filter Section */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Filters</Typography>
                <Grid container spacing={2}>
                    {(userLocation === 'Universal' || userRole === 'owner') ? (
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Location</InputLabel>
                                <Select
                                    value={filters.location}
                                    onChange={(e) => handleFilterChange('location', e.target.value)}
                                    label="Location"
                                >
                                    <MenuItem value="">All Locations</MenuItem>
                                    {availableLocations.map((loc) => (
                                        <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    ) : (
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                label="Location"
                                value={userLocation}
                                disabled
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={8}>
                        <FormControl component="fieldset">
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Time Period
                            </Typography>
                            <RadioGroup
                                row
                                value={filters.timeFilter}
                                onChange={(e) => handleFilterChange('timeFilter', e.target.value)}
                            >
                                <FormControlLabel value="today" control={<Radio />} label="Today" />
                                <FormControlLabel value="custom" control={<Radio />} label="Custom Date Range" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {filters.timeFilter === 'custom' && (
                        <>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    type="date"
                                    value={filters.startDate}
                                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    type="date"
                                    value={filters.endDate}
                                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={fetchReport}
                                disabled={loading}
                                sx={{
                                    px: 3,
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                    '&:hover': {
                                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                                    }
                                }}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Generate Report'}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Summary Cards */}
            {reportData.length > 0 && (
                <>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Summary for {formatPeriod()}
                        {filters.location && ` - ${filters.location}`}
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={filters.location ? 2.4 : 4}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    background: 'linear-gradient(135deg, #66BB6A 0%, #43A047 100%)',
                                    color: 'white'
                                }}
                            >
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                                                Purchased
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                                +{summary.totalPurchased}
                                            </Typography>
                                        </Box>
                                        <TrendingUpIcon sx={{ fontSize: 36, opacity: 0.8 }} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={filters.location ? 2.4 : 4}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    background: 'linear-gradient(135deg, #EF5350 0%, #E53935 100%)',
                                    color: 'white'
                                }}
                            >
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                                                Sold
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                                -{summary.totalSold}
                                            </Typography>
                                        </Box>
                                        <TrendingDownIcon sx={{ fontSize: 36, opacity: 0.8 }} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        {filters.location && (
                            <>
                                <Grid item xs={6} sm={2.4}>
                                    <Card
                                        sx={{
                                            borderRadius: 3,
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                            background: 'linear-gradient(135deg, #29B6F6 0%, #0288D1 100%)',
                                            color: 'white'
                                        }}
                                    >
                                        <CardContent sx={{ p: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                                                        Transfer In
                                                    </Typography>
                                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                                        +{summary.totalTransferIn}
                                                    </Typography>
                                                </Box>
                                                <SwapHorizIcon sx={{ fontSize: 36, opacity: 0.8 }} />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={2.4}>
                                    <Card
                                        sx={{
                                            borderRadius: 3,
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                            background: 'linear-gradient(135deg, #FFA726 0%, #FB8C00 100%)',
                                            color: 'white'
                                        }}
                                    >
                                        <CardContent sx={{ p: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                                                        Transfer Out
                                                    </Typography>
                                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                                        -{summary.totalTransferOut}
                                                    </Typography>
                                                </Box>
                                                <SwapHorizIcon sx={{ fontSize: 36, opacity: 0.8 }} />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12} sm={filters.location ? 2.4 : 4}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    background: parseFloat(summary.netMovement) >= 0
                                        ? 'linear-gradient(135deg, #42A5F5 0%, #1E88E5 100%)'
                                        : 'linear-gradient(135deg, #FF7043 0%, #F4511E 100%)',
                                    color: 'white'
                                }}
                            >
                                <CardContent sx={{ p: 2 }}>
                                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                                        Net Movement
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        {parseFloat(summary.netMovement) >= 0 ? '+' : ''}{summary.netMovement}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Data Table */}
                    <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }} />
                                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Product Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Quantity</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Current Stock</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Order Count</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportData.map((item, index) => (
                                    <React.Fragment key={index}>
                                        <TableRow hover>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRowExpand(index)}
                                                >
                                                    {expandedRow === index ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>{item.productName}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={
                                                        item.type === 'purchase' ? 'Purchase' :
                                                        item.type === 'sale' ? 'Sale' :
                                                        item.type === 'transfer_in' ? 'Transfer In' : 'Transfer Out'
                                                    }
                                                    color={
                                                        item.type === 'purchase' ? 'success' :
                                                        item.type === 'sale' ? 'error' :
                                                        item.type === 'transfer_in' ? 'info' : 'warning'
                                                    }
                                                    size="small"
                                                    icon={
                                                        item.type === 'purchase' ? <TrendingUpIcon /> :
                                                        item.type === 'sale' ? <TrendingDownIcon /> :
                                                        <SwapHorizIcon />
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    color={
                                                        (item.type === 'purchase' || item.type === 'transfer_in')
                                                            ? 'success.main' : 'error.main'
                                                    }
                                                    sx={{ fontWeight: 'bold' }}
                                                >
                                                    {(item.type === 'purchase' || item.type === 'transfer_in')
                                                        ? `+${item.quantity}` : item.quantity}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={item.currentStock.toFixed(2)}
                                                    color={getStockColor(item.currentStock)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{item.orderCount}</TableCell>
                                            <TableCell>
                                                {item.type === 'sale'
                                                    ? `₹${item.revenue.toFixed(2)}`
                                                    : item.type === 'purchase'
                                                    ? (item.cost ? `₹${item.cost.toFixed(2)}` : 'N/A')
                                                    : '-'
                                                }
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                                                <Collapse in={expandedRow === index} timeout="auto" unmountOnExit>
                                                    <Box sx={{ margin: 2 }}>
                                                        <Typography variant="h6" gutterBottom component="div">
                                                            {(item.type === 'transfer_in' || item.type === 'transfer_out')
                                                                ? 'Transfer Details' : 'Order Details'}
                                                        </Typography>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Date</TableCell>
                                                                    {item.type === 'sale' && <TableCell>Party Name</TableCell>}
                                                                    {(item.type === 'transfer_in' || item.type === 'transfer_out') && (
                                                                        <>
                                                                            <TableCell>From</TableCell>
                                                                            <TableCell>To</TableCell>
                                                                        </>
                                                                    )}
                                                                    <TableCell>Quantity</TableCell>
                                                                    {(item.type === 'sale' || item.type === 'purchase') && (
                                                                        <>
                                                                            <TableCell>Rate</TableCell>
                                                                            <TableCell>Amount</TableCell>
                                                                        </>
                                                                    )}
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {item.orders && item.orders.map((order, orderIndex) => (
                                                                    <TableRow key={orderIndex}>
                                                                        <TableCell>{formatDate(order.orderDate)}</TableCell>
                                                                        {item.type === 'sale' && (
                                                                            <TableCell>{order.partyName || 'N/A'}</TableCell>
                                                                        )}
                                                                        {(item.type === 'transfer_in' || item.type === 'transfer_out') && (
                                                                            <>
                                                                                <TableCell>
                                                                                    <Chip label={order.fromLocation} size="small" color="error" variant="outlined" />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Chip label={order.toLocation} size="small" color="success" variant="outlined" />
                                                                                </TableCell>
                                                                            </>
                                                                        )}
                                                                        <TableCell>{order.quantity}</TableCell>
                                                                        {(item.type === 'sale' || item.type === 'purchase') && (
                                                                            <>
                                                                                <TableCell>{order.rate ? `₹${order.rate}` : 'N/A'}</TableCell>
                                                                                <TableCell>
                                                                                    {item.type === 'sale'
                                                                                        ? `₹${order.amount.toFixed(2)}`
                                                                                        : (order.rate ? `₹${(order.quantity * order.rate).toFixed(2)}` : 'N/A')
                                                                                    }
                                                                                </TableCell>
                                                                            </>
                                                                        )}
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {/* Empty State */}
            {!loading && reportData.length === 0 && (
                <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        No data to display
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Select filters and click "Generate Report" to view stock movement data
                    </Typography>
                </Paper>
            )}


            {/* Long Date Range Warning Dialog */}
            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ open: false, onConfirm: null })}
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 600 }}>
                    Long Date Range Warning
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <DialogContentText>
                        You are generating a report for more than 1 month. This may take some time to process and could include a large amount of data.
                        <br /><br />
                        Do you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setConfirmDialog({ open: false, onConfirm: null })}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDialog.onConfirm}
                        variant="contained"
                        color="warning"
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default StockMovementReport;
